import type {
  Apartment,
  EffectiveKeyPickup,
  PickupMethod,
  Property,
  Room,
} from "@/types/property";

// Just the columns the resolver reads, so a partial select (Brain) fits too.
export type PickupRoom = Partial<
  Pick<
    Room,
    | "pickup_method"
    | "pickup_instructions"
    | "keynest_partner_shop_name"
    | "keynest_partner_shop_address"
    | "keynest_access_url"
    | "keynest_pickup_code"
  >
>;
export type PickupApartment = Partial<
  Pick<
    Apartment,
    | "pickup_method"
    | "pickup_instructions"
    | "keynest_pickup_location"
    | "keynest_pickup_address"
  >
>;
export type PickupProperty = Partial<
  Pick<
    Property,
    | "pickup_method"
    | "pickup_instructions"
    | "keynest_pickup_location"
    | "keynest_pickup_address"
    | "keynest_access_url"
  >
>;

/**
 * Resolve the effective key pickup across the inheritance chain.
 * Priority: room override → apartment override → property default — for the
 * method, location, address, and instructions alike, each resolved on its own.
 * Room location/address overrides reuse the legacy
 * keynest_partner_shop_name/address columns; method + instructions use the
 * room's own pickup_method / pickup_instructions.
 * The KeyNest access link/code is intentionally NOT resolved here — it stays
 * per-room (rooms.keynest_access_url / keynest_access_code).
 */
export function resolveEffectiveKeyPickup(args: {
  room?: PickupRoom | null;
  apartment?: PickupApartment | null;
  property?: PickupProperty | null;
}): EffectiveKeyPickup {
  const { room, apartment, property } = args;
  const filled = (s: string | null | undefined) => !!(s && s.trim());

  // Method + instructions resolve room override → apartment → property default.
  const method: PickupMethod =
    room?.pickup_method ??
    apartment?.pickup_method ??
    property?.pickup_method ??
    "keynest";
  const instructions =
    room?.pickup_instructions?.trim() ||
    apartment?.pickup_instructions?.trim() ||
    property?.pickup_instructions?.trim() ||
    null;

  // Location and address each resolve room → apartment → property on their
  // own. A room that only names a shop (e.g. synced from KeyNest, which can
  // leave the address blank) must still inherit the address the admin form
  // shows as "Inherits: …" — resolving the pair as a unit dropped it.
  const levels = [
    {
      source: "room" as const,
      location: room?.keynest_partner_shop_name,
      address: room?.keynest_partner_shop_address,
    },
    {
      source: "apartment" as const,
      location: apartment?.keynest_pickup_location,
      address: apartment?.keynest_pickup_address,
    },
    {
      source: "property" as const,
      location: property?.keynest_pickup_location,
      address: property?.keynest_pickup_address,
    },
  ];
  const locationLevel = levels.find((l) => filled(l.location));
  const addressLevel = levels.find((l) => filled(l.address));
  const location = locationLevel?.location ?? null;
  const address = addressLevel?.address ?? null;
  const source: EffectiveKeyPickup["source"] =
    (locationLevel ?? addressLevel)?.source ?? "none";

  return { method, location, address, instructions, source };
}

/**
 * Shown to a tenant for a KeyNest pickup that has no instructions of its own.
 * Never written to the database: the admin form pre-fills it and stores
 * nothing while it is unchanged, so every tenant reads it in their language.
 */
export const KEYNEST_DEFAULT_INSTRUCTIONS = {
  en: "Tell the shopkeeper you would like to collect a KeyNest key and give them the code.",
  de: "Sag dem Shop-Mitarbeiter, dass du einen KeyNest-Schlüssel abholen möchtest, und nenne ihm den Code.",
} as const;

export function isKeynestDefaultInstructions(
  text: string | null | undefined
): boolean {
  const trimmed = text?.trim();
  return (
    !!trimmed &&
    (Object.values(KEYNEST_DEFAULT_INSTRUCTIONS) as string[]).includes(trimmed)
  );
}

export type KeyPickupGap =
  | "method"
  | "location"
  | "address"
  | "instructions"
  | "keynestLink"
  | "keynestCode";

/**
 * Mandatory key-pickup details a room is still missing (COL-607): method,
 * location, address and instructions always, plus the KeyNest link and pickup
 * code for a KeyNest pickup. Judged on what the tenant would actually see, so
 * anything inherited from the apartment or property counts as filled.
 */
export function keyPickupGaps(args: {
  room?: PickupRoom | null;
  apartment?: PickupApartment | null;
  property?: PickupProperty | null;
}): KeyPickupGap[] {
  const { room, apartment, property } = args;
  const filled = (s: string | null | undefined) => !!(s && s.trim());
  const pickup = resolveEffectiveKeyPickup(args);
  const gaps: KeyPickupGap[] = [];
  if (!(
    room?.pickup_method ??
    apartment?.pickup_method ??
    property?.pickup_method
  ))
    gaps.push("method");
  if (!filled(pickup.location)) gaps.push("location");
  if (!filled(pickup.address)) gaps.push("address");
  // KeyNest always has instructions: the default text covers an empty field.
  if (pickup.method !== "keynest" && !filled(pickup.instructions))
    gaps.push("instructions");
  if (pickup.method === "keynest") {
    if (
      !filled(room?.keynest_access_url) &&
      !filled(property?.keynest_access_url)
    )
      gaps.push("keynestLink");
    if (!filled(room?.keynest_pickup_code)) gaps.push("keynestCode");
  }
  return gaps;
}
